/**
 * Lo que el NAVEGADOR puede comprobar de las guardas de imagen de SPEC 14, en un
 * solo sitio.
 *
 * Quien manda es el servidor y sus cuatro guardas: Kestrel corta el cuerpo, el
 * almacen corta el archivo, los BYTES deciden si es una imagen de verdad y el
 * convertidor tiene su propio tope. Nada de lo que hay aqui autoriza nada.
 *
 * Lo que compra adelantarse es no subir 8 MB por una conexion de telefono para
 * recibir un 400 cuarenta segundos despues. Eso es todo, y es bastante.
 *
 * Vive en `domain/` y no dentro de una pantalla porque ya son DOS las pantallas
 * que suben imagenes al mismo almacen —las capturas de una operacion y el
 * material de Social Data—, y un tope escrito dos veces es un tope que algun dia
 * dira dos cosas distintas.
 */

/** SPEC 14, segunda guarda: el tope por ARCHIVO. */
export const MAX_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Se filtra por el tipo que DECLARA el navegador, que es un primer filtro
 * amable y no una comprobacion: un `.png` que por dentro es un texto llega hasta
 * el servidor y vuelve con su mensaje (tercera guarda).
 */
export const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

/**
 * Las variantes que se ofrecen en un `srcset`, con su ancho REAL.
 *
 * Vive aqui y no dentro del hook porque es la unica parte de esto que se puede
 * probar sin un navegador: el hook solo le pone las URL delante.
 *
 * DOS REGLAS, Y LAS DOS SON FALLOS YA PAGADOS:
 *
 * 1. El descriptor `w` es el ancho del ARCHIVO, no el tope de la variante. El
 *    convertidor no amplia nunca, asi que un grafico de 1200 px da un `view` de
 *    1200. Declararlo como 1920 le dice al navegador que hay mas pixeles de los
 *    que hay, y entonces elige corto.
 * 2. Una imagen mas estrecha que la miniatura da dos archivos del MISMO ancho, y
 *    dos candidatos con el mismo descriptor es una eleccion a cara o cruz. En ese
 *    caso se ofrece solo `view`, que es el original tal cual.
 */
export const VARIANT_CAPS = { thumb: 400, view: 1920 } as const;

export type SrcSetCandidate = { variant: "thumb" | "view"; width: number };

export function srcSetCandidates(viewWidth: number): SrcSetCandidate[] {
  /* Un ancho que no sirve -cero, negativo, NaN- no puede producir un descriptor
     `0w`: el navegador DESCARTA ese candidato y la imagen no carga, que es peor
     que cargarla blanda. Con medida desconocida se ofrece solo `view` declarado
     con su tope, que es lo mejor que se puede decir sin saber. La base lo impide
     con `check (width > 0)`, pero `thumbSize` ya se defiende igual y una fila
     rota no deja la pantalla en blanco por un descuido de aritmetica. */
  if (!Number.isFinite(viewWidth) || viewWidth <= 0) {
    return [{ variant: "view", width: VARIANT_CAPS.view }];
  }

  const thumb = Math.min(VARIANT_CAPS.thumb, viewWidth);
  const view = Math.min(VARIANT_CAPS.view, viewWidth);

  if (thumb >= view) return [{ variant: "view", width: view }];

  return [
    { variant: "thumb", width: thumb },
    { variant: "view", width: view },
  ];
}
